import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  scalarOf,
  type FilterFieldOption,
  type FilterValueInputRenderer,
} from '~/components/ui';
import { FilterRowsPanel } from '~/components/ui/FilterRowsPanel';
import { useGetPropertyValues } from '~/api/product-properties';
import { useGetProductCategories } from '~/api/product';
import type { PlatformProductCategory } from '~/graphql/graphql';
import {
  isPropertyField,
  propertyKeyOf,
  type ProductFilterRow,
} from '~/pages/my-products/lib/filterClauses';
import {
  initialValueForField,
  newProductFilterRow,
} from '~/pages/my-products/lib/myProductsFilterFields';
import {
  categoryOf,
  changeCategory,
  withCategory,
  withoutCategory,
} from '~/pages/my-products/lib/myProductsFilterRows';

function PropertyValueSelect({
  name,
  category,
  propertyKey,
  value,
  onChange,
}: {
  name: string;
  category?: PlatformProductCategory;
  propertyKey: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('my-product');
  const { data } = useGetPropertyValues(category, propertyKey);

  const options = useMemo(() => {
    const fetchedOptions =
      data?.productPropertyValues?.map((propertyValue) => ({
        label: propertyValue.displayValue,
        value: propertyValue.value,
      })) ?? [];

    return [{ label: t('filter.property_all'), value: '' }, ...fetchedOptions];
  }, [data, t]);
  return (
    <Select
      name={name}
      value={value}
      options={options}
      onChange={(_, selectedValue) => onChange(selectedValue)}
      containerClassName="flex-1 min-w-0"
    />
  );
}

type MyProductsFilterPanelProps = {
  readonly fieldOptions: FilterFieldOption[];
  readonly rows: ProductFilterRow[];
  readonly maxHeight?: number;
  readonly bottomContent?: ReactNode;
  readonly onRowsChange: (rows: ProductFilterRow[]) => void;
  readonly onClearAll: () => void;
};

export function MyProductsFilterPanel({
  fieldOptions,
  rows,
  maxHeight,
  bottomContent,
  onRowsChange,
  onClearAll,
}: MyProductsFilterPanelProps) {
  const { t } = useTranslation('my-product');
  const category = categoryOf(rows);
  const { data: categoriesData } = useGetProductCategories();

  const valueInputFor = (
    field: string
  ): FilterValueInputRenderer | undefined =>
    isPropertyField(field)
      ? (value, onChange, name) => (
          <PropertyValueSelect
            name={name}
            category={category}
            propertyKey={propertyKeyOf(field)}
            value={String(scalarOf(value))}
            onChange={onChange}
          />
        )
      : undefined;

  return (
    <FilterRowsPanel
      namespace="my-product"
      keyPrefix="filter"
      rows={withoutCategory(rows)}
      fieldOptions={fieldOptions}
      newRow={newProductFilterRow}
      initialValueFor={(field) => initialValueForField(fieldOptions, field)}
      valueInputFor={valueInputFor}
      maxHeight={maxHeight}
      bottomContent={bottomContent}
      topContent={
        <Select
          name="category"
          value={category ?? ''}
          options={[
            { label: t('filter.all.categories'), value: '' },
            ...(categoriesData?.platformProductCategories ?? []),
          ]}
          onChange={(_, value) =>
            onRowsChange(changeCategory(rows, value || undefined))
          }
        />
      }
      onRowsChange={(visibleRows) =>
        onRowsChange(withCategory(visibleRows, category))
      }
      onClearAll={onClearAll}
    />
  );
}
